import { convertKeysToCamelCase } from "@/services/api-data-mapping";
import {
  getFullName,
  getGroupByDate,
  getNameInitials,
  getParsedJson,
} from "@/services/generic.methods";
import { createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import { DummyData } from "./dummy-data";
import { avatarType, notificationTypes, textTypes, titleTypes } from "./types";
import storage from "@/services/storage";

/**
 * @name @getNotificationText
 * @description this will return text to be shown for the notification
 * @params notification object
 **/

export const getStaticExportName = (fileName = "") => {
  const profile = JSON.parse(storage.getItem("Profile"));
  if (fileName) {
    let text = fileName.split(".");
    let ext = text[text.length - 1];
    return `${profile?.CompanyName.trim()} Leads.${ext}`;
  }
};
const getNotificationText = (notification) => {
  switch (notification.type) {
    case notificationTypes.leads.assinged:
      const isCurrentUser =
        +notification.loggedInUserId === +notification?.assignee?.id;
      const selfAssign =
        +notification?.from?.id === +notification?.assignee?.id;
      return textTypes.leads[notification.type](
        notification?.assignee?.fullName,
        notification?.from?.fullName,
        isCurrentUser,
        selfAssign
      );
    case notificationTypes.leads.received:
    case notificationTypes.leads.optin:
      return notification.description;
    case notificationTypes.leads.statusChanged:
      return textTypes.leads[notification.type](notification?.from?.name);
    case notificationTypes.leads.sms:
      return textTypes.leads[notification.type](notification?.from?.name);
    case notificationTypes.leads.leadFailed:
      return textTypes.leads[notification.type](notification?.assignee);
    case notificationTypes.leads.leadProcessing:
      return textTypes.leads[notification.type](notification?.assignee);
    case notificationTypes.leads.export:
      return textTypes.leads[notification.type](
        getStaticExportName(notification.data)
      );
    case notificationTypes.leads.bulkAssigned:
      const selfAssigned =
        +notification?.from?.id === +notification?.assignee?.id;
      return textTypes.leads[notification.type](
        notification.data,
        selfAssigned
      );
    case notificationTypes.leads.emailReceived:
      return notification.description;
    default:
    // return textTypes.leads[notification.type]();
  }
};

const getNotificationMessage = (notification) => {
  switch (notification.type) {
    case notificationTypes.leads.added:
      if (notification.data?.count && notification.data?.leadImportedBy) {
        const selfAssign =
          +notification.loggedInUserId ===
          +notification?.data?.leadImportedBy?.id;
        return {
          text: textTypes.leads[notification.type](
            notification?.data,
            selfAssign
          ),
        };
      }
      return {
        text: notification.description,
        sender: {
          name: notification.from?.fullName,
        },
      };
    case notificationTypes.leads.sms:
      return {
        text: notification?.data?.text,
        sender: {
          name: notification.from?.fullName,
        },
      };
    case notificationTypes.leads.emailReceived:
      let { data, from } = notification;
      let info = getParsedJson(data?.text);
      let subject = info?.subject?.replace("Re:", "");
      let isNameAvailable = from?.firstName || from?.lastName;
      return {
        text: subject || "(no subject)",
        sender: {
          name: isNameAvailable ? from?.fullName : from?.email,
        },
      };
    case notificationTypes.leads.assinged:
    case notificationTypes.leads.bulkAssigned:
    case notificationTypes.leads.received:
    case notificationTypes.leads.optin:
      return null;
    case notificationTypes.leads.emailOpened:
    case notificationTypes.leads.emailClicked:
      let leadOpenedByName = notification.from?.firstName
        ? `${notification.from?.firstName} ${notification.from?.lastName || ""}`
        : notification.from?.email || "Recipient";
      let emailType = "Opened";
      if (notification.type == notificationTypes.leads.emailClicked) {
        emailType = "Clicked";
      }
      return {
        text: `Email ${emailType} by ${leadOpenedByName}`,
        sender: {
          name: notification.from?.fullName,
        },
      };
    default:
      return {
        text: notification?.description,
        sender: {
          name: notification.from?.fullName,
        },
      };
  }
};
const getNotificationLabels = (notification) => {
  switch (notification.type) {
    case notificationTypes.leads.optin:
      return ["SMS Opt-in"];

    default:
      return [];
  }
};

const getNotificationAvatar = (notification, icon) => {
  return notification?.from?.avatar
    ? avatarType.avatar
    : icon
      ? avatarType.icon
      : avatarType.initials;
};

const getTitle = (notification) => {
  if (notification?.type === notificationTypes.leads.emailReceived) {
    return titleTypes.leads[notification.type];
  }
  return notification?.title ?? titleTypes.leads[notification.type];
};

const mapNotifications = (notifications) => {
  let notificationsData = notifications?.map((notification) => {
    const parsedData = getParsedJson(notification?.data);
    notification.data = convertKeysToCamelCase(parsedData);
    notification.from =
      notification?.data?.leadAssignedBy ?? notification?.data?.leadInfo;
    notification.assignee = notification?.data?.assigneeUserInfo;
    notification.leadId =
      notification?.data?.leadId ?? notification.data?.leadInfo?.id;

    if (notification?.from) {
      notification.from.fullName = notification?.data?.leadAssignedBy
        ? (notification?.from?.firstName ?? "Anonymous")
        : getFullName(notification?.from);
    }
    if (notification?.assignee) {
      notification.assignee.fullName = getFullName(notification?.assignee);
    }

    let icon = null;
    if (
      notification?.type === notificationTypes?.leads?.assinged ||
      notification?.type === notificationTypes?.leads?.bulkAssigned
    ) {
      icon = null;
    } else if (
      notification?.type === notificationTypes.leads.sms ||
      notification?.type === notificationTypes.leads.smsFailed ||
      notification?.type === notificationTypes.leads.received
    ) {
      icon = "message";
    } else {
      icon = "envelope";
    }
    return {
      ...notification,
      time: moment.utc(notification?.createdOn).local().fromNow(),
      avatar: notification?.from?.avatar,
      icon,
      initials: notification?.from?.fullName
        ? getNameInitials(notification?.from?.fullName)
        : "",
      avatarType: getNotificationAvatar(notification, icon),
      title: getTitle(notification),
      text: getNotificationText(notification),
      message: getNotificationMessage(notification),
      labels: getNotificationLabels(notification),
      isNotificationRead: notification.isRead,
    };
  });
  // Add dateGrouping
  notificationsData?.map((item) => {
    item.dateGroup = getGroupByDate(item.createdOn);
  });
  // Sort by createOn desc
  notificationsData = notificationsData?.sort((a, b) =>
    moment.utc(b.createdOn).diff(moment.utc(a.createdOn))
  );
  return notificationsData ?? [];
};
/**
 * @name @createSlice
 * @description create redux store slice for notifications
 **/
export const notificationsSlice = createSlice({
  name: "notifications",
  initialState: {
    notifications: [],
    count: 0,
    unreadCount: 0,
    pageInfo: { skip: 0, take: 10 },
  },

  reducers: {
    /**
     * @name @setNotificationsToState
     * @description setNotificationsToState to set notification to state
     **/
    setNotificationsToState: (state, action) => {
      // map data as per the component requirement
      // this could be changed with real data
      const tempNotifications = [...state.notifications];
      const newNotifications = mapNotifications(action?.payload);
      tempNotifications.push(...newNotifications);
      state.notifications = tempNotifications;
    },
    setNotificationToState: (state, action) => {
      // map data as per the component requirement
      // this could be changed with real data
      const notifications = [...state.notifications];
      let notification = action?.payload;
      const notificationId = notification?.Id ?? notification?.id;
      notification.id = notificationId;
      notification = {
        ...convertKeysToCamelCase(mapNotifications([notification])[0]),
      };
      const isDuplicate = notifications.some((n) => n.id === notificationId);
      if (isDuplicate) {
        console.log(`Notification with ID ${notificationId} already exists.`);
        return;
      }
      notifications.unshift(notification);
      state.notifications = [...notifications];
    },
    updateNotificationToState: (state, action) => {
      let notification = action?.payload;
      state.notifications = state.notifications.map((notif) => {
        if (notif.id == notification.id) {
          return notification;
        }
        return notif;
      });
    },
    setMarkAsRead: (state, action) => {
      // map data as per the component requirement
      // this could be changed with real data
      let data = [...state.notifications];
      const notificationId = action?.payload?.Id ?? action?.payload?.id;
      data = data.map((item) => {
        if (item.id === notificationId) {
          return { ...item, isNotificationRead: 1 };
        }
        return item;
      });

      state.notifications = [...data];
    },
    setPageInfo: (state, action) => {
      const { recordsCount, ...rest } = action.payload;
      state.pageInfo = {
        ...state.pageInfo,
        ...rest,
        skip: state.pageInfo.skip + recordsCount,
      };
    },
  },
});

export const {
  setNotificationsToState,
  setNotificationToState,
  updateNotificationToState,
  setMarkAsRead,
  setPageInfo,
} = notificationsSlice.actions;

/**
 * These function called thunk and allows to perform async logic
 * It can be dispatched like a regular action: `dispatch(data)`
 **/
export const getNotificationsAsync = (payload) => (dispatch) => {
  // here we can add the fetch/ws to get the data and dispatch to reducers
  dispatch(setNotificationsToState([...payload]));
  return Promise.resolve([...payload]);
};

/* called a selector and allows to select values from the state */
export const selectNotifications = (state) => {
  return state.notifications;
};
export const selectNotificationsCount = (state) => {
  return state.notifications?.notifications?.length;
};
export const getPageInfo = (state) => {
  return state.notifications?.pageInfo;
};
export const selectNotificationsUnreadCount = (state) => {
  return state.notifications?.notifications?.filter(
    (notification) => !notification?.isUnread
  )?.length;
};

export default notificationsSlice.reducer;
